import theme from "../styles/theme";
import React, { useState, useRef, useEffect } from "react";
import { components } from "react-select";
import { useNavigate } from "react-router-dom";

import "../styles-arabic/search-input.rtl.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";

import { searchAPI } from "../axios/search-axios";

import "react-simple-keyboard/build/css/index.css";

import { toast } from "react-toastify";
import { auto } from "@popperjs/core";

import { debounce } from "lodash";
import { SEMANTIC_SEARCH_FEATURE_ENABLED } from "../config/featureFlags";
import { trackGoogleAnalyticsEvent } from "../googleAnalytics";

const TOAST_IDS = {
  emptySearch: "search-input-empty",
  serverError: "search-input-server-error",
  noResponse: "search-input-no-response",
  unexpectedError: "search-input-unexpected-error",
  noResults: "search-input-no-results",
  saveError: "search-input-save-error",
  storageError: "search-input-storage-error",
};

const BASE_TOAST_OPTIONS = {
  position: "top-center",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: false,
};

const useSearchInput = ({
  searchString,
  originalSearchString,
  setSearchResults,
  setSearchResultSets,
  setCurrentPage,
  setAnalytics,
  setLoading,
  autocompleteRef,
  setRagSummary,
}) => {
  const parseStoredValue = (value) => {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  };

  const getStoredValue = (key) => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    return parseStoredValue(stored);
  };

  const getInitialSearchInput = () => {
    if (
      typeof originalSearchString === "string" &&
      originalSearchString.trim().length > 0
    ) {
      return originalSearchString;
    }

    const storedOriginal = getStoredValue("originalSearchTerm");
    if (
      typeof storedOriginal === "string" &&
      storedOriginal.trim().length > 0
    ) {
      return storedOriginal;
    }

    const storedSearchTerm = getStoredValue("searchTerm");
    if (
      typeof storedSearchTerm === "string" &&
      storedSearchTerm.trim().length > 0
    ) {
      return storedSearchTerm;
    }

    if (typeof searchString === "string" && searchString.trim().length > 0) {
      return searchString;
    }

    return "";
  };

  const filterStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "#FFFFFF",
      border: "1px solid #CCCCCC",
      borderRadius: "36px",
      marginRight: "0.7em",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "1em",
      fontWeight: 400,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isSelected
        ? theme.colors.mainRed
        : isFocused
        ? theme.colors.mainGrayBackground
        : undefined,
    }),
  };

  const [searchInput, setSearchInput] = useState(getInitialSearchInput);
  const [isHovered, setIsHovered] = useState(false);
  const [matchType, setMatchType] = useState(
    localStorage.getItem("matchType")
      ? localStorage.getItem("matchType").replace(/^"(.*)"$/, "$1")
      : "any"
  );
  const [semanticEnabled, setSemanticEnabled] = useState(() => {
    const storedValue = localStorage.getItem("semanticSearchEnabled");
    return storedValue ? JSON.parse(storedValue) : false;
  });
  const [keyboardVisibility, setkeyboardVisibility] = useState(false);
  const [layoutName, setLayoutName] = useState("default");

  const navigate = useNavigate();
  const keyboard = useRef();

  useEffect(() => {
    if (
      typeof originalSearchString === "string" &&
      originalSearchString.trim().length > 0
    ) {
      setSearchInput((currentValue) => {
        if (currentValue === originalSearchString) {
          return currentValue;
        }
        if (keyboard && keyboard.current) {
          keyboard.current.setInput(originalSearchString);
        }
        return originalSearchString;
      });
    }
  }, [originalSearchString]);

  const handleMatchTypeChange = (event) => {
    setMatchType(event.value);
    localStorage.setItem("matchType", JSON.stringify(event.value));
  };

  const handleSemanticToggle = (checked) => {
    setSemanticEnabled(checked);
    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      localStorage.setItem(
        "semanticSearchEnabled",
        JSON.stringify(checked)
      );
    }
  };

  const lang = localStorage.getItem("lang");
  const noResultsMessage =
    lang === "ar" ? "الرجاء إدخال كلمة بحث" : "No results found";

  const handleSearch = async () => {
    setLoading(true);

    //Send the search term to the backend if string is not empty

    if (searchInput === "") {
      setLoading(false);
      localStorage.clear();
      toast.warn("Please enter a search term", {
        ...BASE_TOAST_OPTIONS,
        rtl: lang === "ar",
        toastId: TOAST_IDS.emptySearch,
      });
      return;
    }
    let searchBackend = {
      search_term: searchInput,
      match_type: matchType,
    };

    if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
      searchBackend.semantic_search_enabled = semanticEnabled;
    }

    // Call the search API
    try {
      let res;
      try {
        res = await searchAPI.put("", searchBackend);
      } catch (apiError) {
        console.error("Failed to fetch search results", apiError);

        if (apiError.response) {
          // The request was made, and the server responded with a status code
          console.error("Server Response:", apiError.response.data);
          toast.error(
            `Server error: ${apiError.response.data.message || "Unexpected error"}`,
            {
              ...BASE_TOAST_OPTIONS,
              rtl: lang === "ar",
              toastId: TOAST_IDS.serverError,
            }
          );
        } else if (apiError.request) {
          // The request was made but no response was received
          console.error("No response received. Request details:", apiError.request);
          toast.error("No response from server.", {
            ...BASE_TOAST_OPTIONS,
            rtl: lang === "ar",
            toastId: TOAST_IDS.noResponse,
          });
        } else {
          // Something else went wrong while setting up the request
          console.error("Unexpected error:", apiError.message);
          toast.error("An unexpected error occurred.", {
            ...BASE_TOAST_OPTIONS,
            rtl: lang === "ar",
            toastId: TOAST_IDS.unexpectedError,
          });
        }
        throw apiError; // Rethrow the error to be caught by the outer catch block
      }
      
      const originalSearchTerm = searchInput;
      const translatedSearchTerm = res.data?.translated_search_term;
      const effectiveSearchTerm =
        translatedSearchTerm && translatedSearchTerm.trim().length > 0
          ? translatedSearchTerm
          : originalSearchTerm;

      if (
        translatedSearchTerm &&
        translatedSearchTerm.trim().length > 0 &&
        translatedSearchTerm !== originalSearchTerm
      ) {
        setSearchInput(translatedSearchTerm);
        if (keyboard && keyboard.current) {
          keyboard.current.setInput(translatedSearchTerm);
        }
      }

      // console.log("check search result",res);
      if (res.data.docs.length === 0) {
        setLoading(false);
        if(setSearchResults) setSearchResults([]);
        if(setSearchResultSets) setSearchResultSets([]);
        toast.warn(`${noResultsMessage}`, {
          ...BASE_TOAST_OPTIONS,
          rtl: lang === "ar",
          toastId: TOAST_IDS.noResults,
        });
        return;
      }

      // Save search results to local storage
      try {
        localStorage.setItem("searchResults", JSON.stringify(res.data.docs));
      } catch (e) {
        if (e instanceof DOMException && e.name === "QuotaExceededError") {
          try {
            localStorage.removeItem("searchResults");
            localStorage.clear();
            // Set other items back to local storage
            localStorage.setItem("searchTerm", JSON.stringify(effectiveSearchTerm));
            localStorage.setItem("matchType", JSON.stringify(matchType));
            if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
              localStorage.setItem(
                "semanticSearchEnabled",
                JSON.stringify(semanticEnabled)
              );
            }
            if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
              localStorage.setItem(
                "ragSummary",
                JSON.stringify(res.data.rag || "")
              );
            }
            localStorage.setItem(
              "searchResults",
              JSON.stringify(res.data.docs)
            );
            // Success handling, possibly a success message or log
          } catch (e) {
            toast.error("Could not save search results. Please try again.", {
              ...BASE_TOAST_OPTIONS,
              rtl: lang === "ar",
              toastId: TOAST_IDS.saveError,
            });
            localStorage.clear();
          }
        } else {
          toast.error("An error occurred. Please try again.", {
            ...BASE_TOAST_OPTIONS,
            rtl: lang === "ar",
            toastId: TOAST_IDS.storageError,
          });
          localStorage.clear();
          throw e;
        }
      }

      localStorage.setItem("analytics", JSON.stringify(res.data.analytics)); //No analytics for now
      localStorage.setItem("searchItemsSet", JSON.stringify(false));
      localStorage.setItem("searchTerm", JSON.stringify(effectiveSearchTerm));
      localStorage.setItem("matchType", JSON.stringify(matchType));
      if (translatedSearchTerm && translatedSearchTerm.trim().length > 0) {
        localStorage.setItem("originalSearchTerm", JSON.stringify(originalSearchTerm));
      } else {
        localStorage.removeItem("originalSearchTerm");
      }

      const langValue = localStorage.getItem("lang");
      const collNameKey = langValue === "ar" ? "coll_name_arabic" : "coll_name";
      const collLocationKey =
        langValue === "ar" ? "coll_location_arabic" : "coll_location";
      const collSourceKey =
        langValue === "ar" ? "coll_source_arabic" : "coll_source";

      const baseFilters = {
        search_term: effectiveSearchTerm,
        match_type: matchType,
        sort_type: "desc",
        [collNameKey]: [],
        [collLocationKey]: [],
        [collSourceKey]: [],
      };

      if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
        baseFilters.semantic_search_enabled = semanticEnabled;
      }
      localStorage.setItem("filters", JSON.stringify(baseFilters));

      if (typeof setAnalytics === "function") {
        setAnalytics(res.data.analytics);
      }
      if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
        localStorage.setItem(
          "semanticSearchEnabled",
          JSON.stringify(semanticEnabled)
        );
      }
      if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
        localStorage.setItem(
          "ragSummary",
          JSON.stringify(res.data.rag || "")
        );
      }

      if (setSearchResults) {
        setSearchResults(res.data.docs);
        setSearchResultSets((prevSearchResultSets) => ({
          [1]: res.data.docs,
        }));
      }

      if (setCurrentPage) {
        setCurrentPage(1);
      }

      trackGoogleAnalyticsEvent("search", {
        // GA4's standard param for site search.
        // We store the exact user-entered input here.
        search_term: originalSearchTerm,
        // Custom params for Fihris search behavior.
        effective_search_term: effectiveSearchTerm,
        translated_search_term:
          translatedSearchTerm && translatedSearchTerm.trim().length > 0
            ? translatedSearchTerm
            : null,
        translation_applied:
          translatedSearchTerm &&
          translatedSearchTerm.trim().length > 0 &&
          translatedSearchTerm !== originalSearchTerm,
        match_type: matchType,
        result_count: res.data.count ?? res.data.docs.length,
      });

      const filtersResetToken = Date.now();

      if (SEMANTIC_SEARCH_FEATURE_ENABLED && setRagSummary) {
        setRagSummary(res.data.rag || "");
      }

      const navigationState = {
        searchString: searchInput,
        searchResults: res.data.docs,
        analytics: res.data.analytics,
        totalCount: res.data.count,
        searchAfter: res.data.search_after,
        pageNumber: 1,
        filtersResetToken,
      };

      if (SEMANTIC_SEARCH_FEATURE_ENABLED) {
        navigationState.rag = res.data.rag || "";
      }

      navigate("/search-results", {
        state: {
          searchString: effectiveSearchTerm,
          translatedSearchString: translatedSearchTerm || null,
          originalSearchString: originalSearchTerm,
          searchResults: res.data.docs,
          analytics: res.data.analytics,
          totalCount: res.data.count,
          searchAfter: res.data.search_after,
          pageNumber: 1,
          filtersResetToken,
        },
      });

      setLoading(false);

    } catch (error) {
      setLoading(false);
      if (error.code === "ECONNABORTED") {
        console.error("Request timed out. Server error.");
      }
    }
  };
    
  library.add(faChevronDown);

  const filterOptions =
    lang === "ar"
      ? [
          {
            label: "أي كلمة",
            value: "any",
          },
          {
            label: "جميع الكلمات",
            value: "all",
          },
          {
            label: "الكلمة الدقيقة",
            value: "exact",
          },
        ]
      : [
          {
            label: "Any Word",
            value: "any",
          },
          {
            label: "All Words",
            value: "all",
          },
          {
            label: "Exact Word",
            value: "exact",
          },
        ];

  const Placeholder = (props) => {
    return <components.Placeholder {...props} />;
  };

  const CaretDownIcon = () => {
    return <FontAwesomeIcon icon="chevron-down" className="option-arrow" />;
  };

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <CaretDownIcon />
      </components.DropdownIndicator>
    );
  };

  // ----------------Arabic Keyboard----------------

  const toggleKeyboard = () => {
    setkeyboardVisibility(!keyboardVisibility);
  };

  const onKeyPress = (button) => {
    if (button === "{shift}" || button === "{lock}") handleShift();
    if (button === "{enter}") handleSearch();
  };

  const handleShift = () => {
    setLayoutName((prevLayoutName) =>
      prevLayoutName === "default" ? "shift" : "default"
    );
  };

  const matchTypeFromLocalStorage = localStorage.getItem("matchType")
    ? localStorage.getItem("matchType").replace(/^"(.*)"$/, "$1")
    : "any";

  const defaultValueOption = filterOptions.find(
    (option) => option.value === matchTypeFromLocalStorage
  );

  const defaultValue = defaultValueOption ? defaultValueOption : null;

  // Autocomplete feature

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);

  const debouncedAutoComplete = debounce(async (input) => {
    const words = input.trim().split(/\s+/).slice(-5);
    const autocompleteText = words.join(" ");
    if (autocompleteText.length > 2) {
      try {
        const res = await searchAPI.put(
          `autocomplete?autocomplete_text=${autocompleteText}`
        );
        // Filter and clean suggestions
        const filteredSuggestions = res.data.map((suggestion) => {
          let cleanedSuggestion = cleanSuggestion(suggestion);
          if (
            !autocompleteText.startsWith("ال") &&
            cleanedSuggestion.startsWith("ال")
          ) {
            cleanedSuggestion = cleanedSuggestion.replace("ال", "");
          }
          return cleanedSuggestion;
        });
        setSuggestions(filteredSuggestions);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Failed to fetch autocomplete suggestions", error);
        setSuggestions([]);
      }
    } else {
      // Clear if last word too short
      setSuggestions([]);
    }
  }, 250);

  //we get the search value here
  const onChangeInput = async (event) => {
    const input = event.target.value;
    setSearchInput(input);
    //debouncedAutoComplete(input);
    if (keyboard && keyboard.current) keyboard.current.setInput(input);
  };

  const onChangeArabicKeyboard = (input) => {
    setSearchInput(input);
    //debouncedAutoComplete(input);
    if (keyboard && keyboard.current) keyboard.current.setInput(input);
  };

  useEffect(() => {
    return () => {
      debouncedAutoComplete.cancel();
    };
  }, []);

  // Autocomplete dropdown close on click outside

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [autocompleteRef, setShowSuggestions]);

  const handleSuggestionClick = (suggestion) => {
    setShowSuggestions(false);
    // Set the entire input to = suggestion
    setSearchInput(suggestion);
    if (keyboard && keyboard.current) keyboard.current.setInput(suggestion);
  };

  // Cleanup suggestions on Production
  const cleanSuggestion = (suggestion) => {
    // Replace any no. of - or + with a single space
    return suggestion.replace(/[-+]+/g, " ").replace(/\s+/g, " ").trim();
  };

  return {
    searchInput,
    setSearchInput,
    isHovered,
    setIsHovered,
    matchType,
    setMatchType,
    semanticEnabled,
    handleSemanticToggle,
    keyboardVisibility,
    setkeyboardVisibility,
    layoutName,
    setLayoutName,
    filterStyles,
    filterOptions,
    handleSearch,
    handleMatchTypeChange,
    onChangeInput,
    toggleKeyboard,
    onChangeArabicKeyboard,
    onKeyPress,
    handleShift,
    defaultValue,
    Placeholder,
    DropdownIndicator,
    keyboard,
    suggestions,
    setSuggestions,
    showSuggestions,
    setShowSuggestions,
    handleSuggestionClick,
  };
};

export default useSearchInput;
